import { useState, useEffect, useCallback, useRef } from 'react';
import type { JobListing, JobRecommendationsResponse, Skill, SkillMatch, SortBy } from 'job-bank-data-access';
import { getAccessToken } from '@accenturecodefoundry/mfe-pot-shared-auth/core';
import { loadRuntimeConfig } from '../runtime-config';
import { assetBaseUrl } from './asset-base-url';
import { fetchSkills } from './data/skills-api';

// Same memoisation as skills-api.ts — one env.js fetch shared across all
// recommendations calls (pagination, sort, refine) for the module lifetime.
let configPromise: ReturnType<typeof loadRuntimeConfig> | undefined;
const getConfig = () => (configPromise ??= loadRuntimeConfig(assetBaseUrl));

const PAGE_SIZE = 5;

export interface RefineParams {
  skills: Skill[];
  sortBy: SortBy;
  address: string;
  province: string;
  jobTypes: string[];
}

const DEFAULT_PARAMS: RefineParams = {
  skills: [],
  sortBy: 'best-skills-fit',
  address: '',
  province: '',
  jobTypes: [],
};

async function fetchRecommendations(
  params: RefineParams,
  page: number,
): Promise<{ listings: JobListing[]; total: number; matchedSkills: Record<string, SkillMatch[]> }> {
  const { jobBankBffBaseUrl } = await getConfig();
  const token = getAccessToken();
  const url = new URL(`${jobBankBffBaseUrl}/api/v1/recommendations`);
  url.searchParams.set('sortBy', params.sortBy);
  url.searchParams.set('page', String(page));
  url.searchParams.set('pageSize', String(PAGE_SIZE));
  if (params.address) url.searchParams.set('address', params.address);
  if (params.province) url.searchParams.set('province', params.province);
  // jobTypes: no BFF param yet — accepted client-side only
  const res = await fetch(url.toString(), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`BFF ${res.status}`);
  const data = (await res.json()) as JobRecommendationsResponse;
  return { listings: data.listings, total: data.total, matchedSkills: data.matchedSkills };
}

export type JobMatchesPhase =
  | { tag: 'loading' }
  | { tag: 'loading-listings'; skills: Skill[] }
  | { tag: 'ready'; skills: Skill[]; listings: JobListing[]; total: number; page: number; totalPages: number; matchedSkills: Record<string, SkillMatch[]> }
  | { tag: 'error' };

export function useJobMatches() {
  const [phase, setPhase] = useState<JobMatchesPhase>({ tag: 'loading' });
  const [skills, setSkills] = useState<Skill[] | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const mounted = useRef(true);
  // Stored so goToPage preserves all refine params across page changes without
  // needing phase in its dependency array (avoids recreating the callback every render).
  const paramsRef = useRef<RefineParams>(DEFAULT_PARAMS);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadPage = useCallback((params: RefineParams, page: number) => {
    paramsRef.current = params;
    setPhase({ tag: 'loading-listings', skills: params.skills });
    fetchRecommendations(params, page)
      .then(({ listings, total, matchedSkills }) => {
        if (!mounted.current) return;
        setPhase({
          tag: 'ready',
          skills: params.skills,
          listings,
          total,
          page,
          totalPages: Math.ceil(total / PAGE_SIZE),
          matchedSkills,
        });
      })
      .catch(() => {
        if (!mounted.current) return;
        setPhase({ tag: 'error' });
      });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setPhase({ tag: 'loading' });
    setSkills(null);

    fetchSkills()
      .then((s) => { if (!cancelled) setSkills(s); })
      .catch(() => { if (!cancelled) setSkills([]); }); // degrade: hide prompt, don't block listing

    loadPage(paramsRef.current, 1);

    return () => {
      cancelled = true;
    };
  }, [retryCount, loadPage]);

  const retry = useCallback(() => setRetryCount((c) => c + 1), []);

  const goToPage = useCallback(
    (nextPage: number) => {
      loadPage(paramsRef.current, nextPage);
    },
    [loadPage],
  );

  const applyRefine = useCallback(
    (params: RefineParams) => {
      setSkills(params.skills);
      loadPage(params, 1);
    },
    [loadPage],
  );

  return { phase, skills, retry, goToPage, applyRefine };
}
